import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [userSites, setUserSites] = useState([]);
  const [originalUser, setOriginalUser] = useState(null);
  const [originalToken, setOriginalToken] = useState(null);
  const [originalUserSites, setOriginalUserSites] = useState([]);

  useEffect(() => {
    // Check if user is already logged in
    const storedToken = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');
    const storedSites = localStorage.getItem('userSites');
    
    // Check for original user data (for impersonation)
    const storedOriginalUser = localStorage.getItem('originalUser');
    const storedOriginalToken = localStorage.getItem('originalToken');
    const storedOriginalSites = localStorage.getItem('originalUserSites');
    
    if (storedToken && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        // Ensure full_access has a default value
        const userWithDefaults = {
          ...parsedUser,
          full_access: parsedUser.full_access || '1'
        };
        setUser(userWithDefaults);
        setToken(storedToken);
        
        // Load user sites if available
        if (storedSites) {
          const parsedSites = JSON.parse(storedSites);
          setUserSites(parsedSites);
        }
        
        // Load original user data if available (impersonation mode)
        if (storedOriginalUser && storedOriginalToken) {
          try {
            const parsedOriginalUser = JSON.parse(storedOriginalUser);
            setOriginalUser(parsedOriginalUser);
            setOriginalToken(storedOriginalToken);
            if (storedOriginalSites) {
              const parsedOriginalSites = JSON.parse(storedOriginalSites);
              setOriginalUserSites(parsedOriginalSites);
            }
          } catch (error) {
            console.error('Error parsing original user data:', error);
          }
        }
        
        // Optionally verify token with backend
        verifyToken(storedToken, userWithDefaults);
      } catch (error) {
        console.error('Error parsing user data:', error);
        clearAuth();
      }
    }
    
    setIsLoading(false);
  }, []);

  const verifyToken = async (authToken, userData) => {
    try {
      // You can add token verification here if your API supports it
      // const response = await fetch('http://127.0.0.1:8000/api/verify-token', {
      //   headers: { 'Authorization': `Bearer ${authToken}` }
      // });
      // if (!response.ok) throw new Error('Token invalid');
    } catch (error) {
      console.error('Token verification failed:', error);
      clearAuth();
    }
  };

  const login = (userData, authToken, sites = [], isImpersonating = false) => {
    // Debug: Log what's being stored
    console.log('🔐 AuthContext: Storing user data:', userData);
    console.log('🔐 AuthContext: Storing sites:', sites);
    console.log('🔐 AuthContext: Storing token:', authToken);
    console.log('🔐 AuthContext: Is impersonating:', isImpersonating);
    
    // If impersonating, save current user as original user before switching
    if (isImpersonating && user && token) {
      console.log('💾 Saving original user data before impersonation');
      setOriginalUser(user);
      setOriginalToken(token);
      setOriginalUserSites(userSites);
      
      localStorage.setItem('originalUser', JSON.stringify(user));
      localStorage.setItem('originalToken', token);
      localStorage.setItem('originalUserSites', JSON.stringify(userSites));
    }
    
    // Ensure full_access has a default value
    const userWithDefaults = {
      ...userData,
      full_access: userData.full_access || '1'
    };
    
    setUser(userWithDefaults);
    setToken(authToken);
    setUserSites(sites);
    
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('user', JSON.stringify(userWithDefaults));
    localStorage.setItem('userSites', JSON.stringify(sites));
    
    console.log('✅ AuthContext: User data stored successfully');
  };

  const switchBackToOriginal = () => {
    if (originalUser && originalToken) {
      console.log('🔄 Switching back to original user');
      
      setUser(originalUser);
      setToken(originalToken);
      setUserSites(originalUserSites);
      
      localStorage.setItem('authToken', originalToken);
      localStorage.setItem('user', JSON.stringify(originalUser));
      localStorage.setItem('userSites', JSON.stringify(originalUserSites));
      
      // Clear original user data
      setOriginalUser(null);
      setOriginalToken(null);
      setOriginalUserSites([]);
      localStorage.removeItem('originalUser');
      localStorage.removeItem('originalToken');
      localStorage.removeItem('originalUserSites');
      
      console.log('✅ Switched back to original user');
    }
  };

  const logout = () => {
    clearAuth();
  };

  const clearAuth = () => {
    setUser(null);
    setToken(null);
    setUserSites([]);
    setOriginalUser(null);
    setOriginalToken(null);
    setOriginalUserSites([]);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userSites');
    localStorage.removeItem('originalUser');
    localStorage.removeItem('originalToken');
    localStorage.removeItem('originalUserSites');
  };

  const updateUser = (updatedUserData) => {
    const newUserData = { 
      ...user, 
      ...updatedUserData,
      full_access: updatedUserData.full_access || user?.full_access || '1'
    };
    setUser(newUserData);
    localStorage.setItem('user', JSON.stringify(newUserData));
  };

  const value = {
    user,
    token,
    userSites,
    originalUser,
    isImpersonating: !!originalUser,
    login,
    logout,
    updateUser,
    setUserSites,
    switchBackToOriginal,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
